<template>
  <v-card outlined>
    <line-chart
      title="Sessions et articles créés"
      :loading="loading"
      :colors="colors"
      :series="series"
      :labels="labels"
      :yAxis="yAxis"
      :stroke="stroke"
      outlined
    ></line-chart>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-switch
        v-model="frequency"
        hide-details
        true-value="daily"
        false-value="monthly"
        :label="`Regroupement : ${
          frequency === 'daily' ? 'par jour' : 'par mois'
        }`"
      ></v-switch>
    </v-card-actions>
  </v-card>
</template>

<script>
import LineChart from "@/components/common/charts/LineChart";

import useAxios from "@/hooks/useAxios";

import { colors } from "@/utils/constants";
import { getDaysArray, getMonthsArray } from "@/utils/dates";
import { formatNumber } from "@/utils/formatting";

const yAxis = (color = "") => ({
  min: 0,
  forceNiceScale: true,
  labels: {
    formatter: (val) => formatNumber(val),
    style: {
      fontSize: "12px",
      fontWeight: 400,
      ...(color ? { colors: color } : {}),
    },
  },
});

export default {
  name: "StatsByDateChart",
  components: {
    LineChart,
  },
  data() {
    return {
      loading: false,
      forceCall: false,
      frequency: "daily",
      colors: [colors.sessions, colors.chartDefault],
      series: [
        // Order must be the same than in yaxis definition here below
        {
          name: "sessions",
          data: [],
        },
        {
          name: "created",
          data: [],
        },
      ],
      labels: [],
      yAxis: [
        // Order must be the same than in series definitions
        {
          seriesName: "sessions",
          title: {
            text: "Sessions",
            style: { color: colors.sessions },
          },
          ...yAxis(colors.sessions),
        },
        {
          opposite: true,
          seriesName: "created",
          title: {
            text: "Créés",
            style: { color: colors.chartDefault },
          },
          ...yAxis(colors.chartDefault),
        },
      ],
      stroke: {
        show: true,
        curve: "smooth",
        width: [2, 2],
      },
    };
  },
  setup() {
    const { axiosGet } = useAxios();

    return {
      axiosGet,
    };
  },
  methods: {
    async getData() {
      if (
        (this.applyFilters || this.forceCall) &&
        !this.loading &&
        this.startDate &&
        this.startDate.length === 10 &&
        this.endDate &&
        this.endDate.length === 10
      ) {
        this.loading = true;
        const { data } = await this.axiosGet("/audience/stats-by-date-range", {
          start_date: this.startDate,
          end_date: this.endDate,
          sites: this.sites.join(","),
          period: this.frequency,
        });

        this.labels =
          this.frequency === "monthly"
            ? getMonthsArray(this.startDate, this.endDate)
            // The selected end date is inclusive in the API query. Keep the
            // chart labels aligned so its data point is not discarded.
            : getDaysArray(this.startDate, this.endDate, true);

        this.series = [
          // Order must be the same than in chartOptions.yaxis definition
          {
            name: "sessions",
            type: "line",
            data: this.labels.map(
              (date) =>
                data.find((details) => details.date === date)?.sessions ?? 0
            ),
          },
          {
            name: "created",
            type: "column",
            data: this.labels.map(
              (date) =>
                data.find((details) => details.date === date)?.created ?? 0
            ),
          },
        ];
        this.loading = false;

        // Force clicking on button again to re-run that request :
        // (we don't call this dispatch earlier to be sure, as state is async, that all listening components could run their requests)
        this.$store.dispatch("common/updateApplyFilters", false);
      }

      this.forceCall = false;
    },
  },
  computed: {
    applyFilters() {
      return this.$store.getters["common/getApplyFilters"];
    },
    sites() {
      return this.$store.getters["audience/getSites"];
    },
    startDate() {
      return this.$store.getters["audience/getDates"][0];
    },
    endDate() {
      return this.$store.getters["audience/getDates"][1];
    },
  },
  watch: {
    applyFilters() {
      this.getData();
    },
  },
};
</script>
